import { createContext, useContext, useMemo, useState, useCallback } from 'react'
import { recentTransactions, staffTransactions } from '../data/seed'

const LedgerContext = createContext(null)

const nextTxnId = () => `TXN-${Math.floor(1000 + Math.random() * 9000)}`

export function LedgerProvider({ children, demoMode = false }) {
  const [transactions, setTransactions] = useState(() => {
    if (demoMode) {
      return [...recentTransactions, ...staffTransactions].map((t) => ({
        ...t,
        type: t.type || (t.amount >= 0 ? 'Income' : 'Expense'),
        createdAt: t.createdAt || new Date(`${t.date}T10:00:00Z`).getTime(),
        locked: t.locked ?? false,
      }))
    }
    return []
  })

  const [tasks, setTasks] = useState([])

  const [staff, setStaff] = useState([])

  const addStaff = useCallback((member) => {
    setStaff((prev) => [...prev, { ...member, id: `S-${String(prev.length + 1).padStart(2, '0')}`, status: 'Active' }])
  }, [])

  const removeStaff = useCallback((id) => {
    setStaff((prev) => prev.filter((s) => s.id !== id))
  }, [])

  const [docQueue, setDocQueue] = useState([])

  const addTransaction = useCallback((txn) => {
    const ts = Date.now()
    const created = {
      ...txn,
      id: txn.id || nextTxnId(),
      createdAt: ts,
      date: txn.date || new Date(ts).toISOString().slice(0, 10),
      type: txn.type || (txn.amount >= 0 ? 'Income' : 'Expense'),
      locked: false,
    }
    setTransactions((prev) => [created, ...prev])
    return created
  }, [])

  const editTransaction = useCallback((id, patch) => {
    setTransactions((prev) =>
      prev.map((t) => {
        if (t.id !== id || t.locked) return t
        if (Date.now() - t.createdAt > 30 * 60 * 1000) return t
        return { ...t, ...patch }
      }),
    )
  }, [])

  const deleteTransaction = useCallback((id) => {
    setTransactions((prev) =>
      prev.filter((t) => {
        if (t.id !== id) return true
        if (t.locked) return true // cannot delete locked
        if (Date.now() - t.createdAt > 30 * 60 * 1000) return true
        return false
      }),
    )
  }, [])

  const lockTransaction = useCallback((id) => {
    setTransactions((prev) => prev.map((t) => (t.id === id ? { ...t, locked: true } : t)))
  }, [])

  // tasks
  const addTask = useCallback((task) => {
    setTasks((prev) => [
      {
        ...task,
        id: `TASK-${String(prev.length + 1).padStart(2, '0')}`,
        status: 'Pending',
      },
      ...prev,
    ])
  }, [])

  const setTaskStatus = useCallback((id, status) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)))
  }, [])

  const deleteTask = useCallback((id) => {
    setTasks((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // doc queue
  const addDocument = useCallback((doc) => {
    setDocQueue((prev) => [
      {
        ...doc,
        id: `DOC-Q${String(prev.length + 1).padStart(2, '0')}`,
        status: 'Pending Review',
        uploadedAt: new Date().toISOString().slice(0, 10),
        rejectReason: null,
      },
      ...prev,
    ])
  }, [])

  const approveDocument = useCallback((id) => {
    setDocQueue((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: 'Approved', rejectReason: null } : d)),
    )
  }, [])

  const rejectDocument = useCallback((id, reason) => {
    setDocQueue((prev) =>
      prev.map((d) => (d.id === id ? { ...d, status: 'Rejected', rejectReason: reason } : d)),
    )
  }, [])

  // KPI recalculation derived from live transactions
  const kpis = useMemo(() => {
    if (demoMode) {
      return [
        { id: 'income', label: 'Income', value: 45200000, delta: 12 },
        { id: 'expenses', label: 'Expenses', value: 32100000, delta: 5 },
        { id: 'profit', label: 'Profit', value: 13100000, delta: 28 },
      ]
    }
    let income = 0
    let expenses = 0
    for (const t of transactions) {
      if (t.amount >= 0) income += t.amount
      else expenses += Math.abs(t.amount)
    }
    return [
      { id: 'income', label: 'Income', value: income, delta: 12 },
      { id: 'expenses', label: 'Expenses', value: expenses, delta: 5 },
      { id: 'profit', label: 'Profit', value: income - expenses, delta: 28 },
    ]
  }, [transactions, demoMode])

  const value = useMemo(
    () => ({
      transactions,
      kpis,
      addTransaction,
      editTransaction,
      deleteTransaction,
      lockTransaction,
      tasks,
      addTask,
      setTaskStatus,
      deleteTask,
      staff,
      addStaff,
      removeStaff,
      docQueue,
      addDocument,
      approveDocument,
      rejectDocument,
    }),
    [transactions, kpis, addTransaction, editTransaction, deleteTransaction, lockTransaction, tasks, addTask, setTaskStatus, deleteTask, staff, addStaff, removeStaff, docQueue, addDocument, approveDocument, rejectDocument],
  )

  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>
}

export function useLedger() {
  const ctx = useContext(LedgerContext)
  if (!ctx) throw new Error('useLedger must be used within LedgerProvider')
  return ctx
}
